import React from 'react';
import { motion } from 'framer-motion';

const BirthdaySplurgeLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Birthday Splurge Regret: Why We Overspend On Our Special Day
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Birthday Splurge Guilt</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your birthday purchases, understand your celebration spending habits, and reduce post-birthday buyer's remorse.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Birthday Purchase
            </motion.button>
          </div>
          <div className="text-8xl">🎂</div>
        </div>
      </div>
      
      {/* What causes birthday shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Do We Overspend on Our Birthdays?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🎁</span>
              <div>
                <h3 className="font-bold dark:text-white">The "Treat Yourself" Mentality</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The birthday excuse becomes a free pass to indulge in otherwise unjustifiable purchases.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Emotional Milestone</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Birthdays can trigger existential feelings, leading to retail therapy to cope with aging.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👯</span>
              <div>
                <h3 className="font-bold dark:text-white">Social Expectations</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Pressure to have Instagram-worthy celebrations pushes us to spend beyond our means.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Birthday Discounts</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Marketing tactics like special birthday offers make spending seem more justifiable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Birthday Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Birthday Celebration Costs
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Log your self-gifts, party expenses, and special event costs to see the full picture.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your Birthday Guilt Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get a personalized assessment that factors in your budget, emotions, and long-term satisfaction.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Birthday-Specific Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get tailored tips for celebrating meaningfully without the financial hangover.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Plan Better Future Birthdays
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Learn from past celebrations to create more satisfying, budget-friendly birthday traditions.
            </p>
          </div>
        </div>
      </section>
      
      {/* Birthday Splurger Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">What's Your Birthday Splurger Type?</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">👑</span>
              <h3 className="font-bold mt-2 dark:text-white">The Royal Celebrator</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You believe birthdays require living like royalty—luxury hotels, fine dining, and VIP experiences, regardless of your actual budget.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛍️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Self-Gifter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You use your birthday as the ultimate excuse to finally buy all those wishlist items you've been eyeing all year.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎭</span>
              <h3 className="font-bold mt-2 dark:text-white">The Social Showoff</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Your birthday isn't just a celebration—it's content. You plan Instagram-worthy events that often exceed your financial comfort zone.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⏰</span>
              <h3 className="font-bold mt-2 dark:text-white">The Age Anxiety Spender</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You use expensive purchases to distract yourself from thoughts about aging or not meeting life milestones on schedule.
            </p>
          </div>
        </div>
      </section>
      
      {/* Birthday Guilt Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Birthday Spending By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">$235</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average self-spending on birthdays</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">78%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">experience buyer's remorse post-celebration</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3.5×</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">more likely to make impulse purchases</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">42%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">go over budget during birthday month</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Handled Birthday Spending</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to blow $500+ on my birthday weekend, then feel guilty for weeks. After tracking with Guilt Gauge, I found ways to celebrate that felt special without the financial hangover."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jessica M.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Royal Celebrator</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "My 30th birthday triggered major anxiety, so I spent $900 on a designer bag. The guilt lasted longer than the joy. Now I budget for milestone birthdays months in advance."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Marcus L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Age Anxiety Spender</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Birthday Spending</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel guilty after my birthday celebrations?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The "birthday high" often leads to emotional spending decisions followed by a reality check when the celebration ends. The contrast between special-day justifications and everyday financial reality creates guilt, especially when birthday spending impacts your regular budget.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much should I reasonably spend on my birthday?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial advisors suggest allocating no more than 1-2% of your annual income for birthday celebrations. However, the "right" amount varies based on your financial situation, goals, and personal values. Our guilt calculator helps you determine your personal sweet spot.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I celebrate my birthday without overspending?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Plan ahead by setting a birthday budget 2-3 months in advance. Focus on meaningful experiences over material purchases. Consider spreading celebrations across several days with different friend groups to avoid one expensive event. Track your emotions with our calculator to understand your spending triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Are milestone birthdays worth spending more on?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Milestone birthdays can justify somewhat higher spending if planned thoughtfully. The key is intentionality: budget ahead, choose experiences or items with lasting value, and be honest about whether you're spending for yourself or social validation. Our calculator helps evaluate these special celebration expenses.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Have Birthday Joy Without the Financial Hangover?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your birthday purchases and discover your celebration spending style.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Birthday Guilt Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Birthday Spending Guilt</h2>
          <p className="mb-3">
            Birthday spending guilt is the emotional discomfort that follows celebratory purchases or experiences that exceed our budget or aligned values. It often stems from the cultural notion that birthdays should be exceptional and "worth it," regardless of financial impact.
          </p>
          <p className="mb-3">
            Our Birthday Guilt Calculator helps you understand and address these feelings by tracking your emotional state during birthday purchases, analyzing your celebration spending patterns, and providing personalized strategies to enjoy your special day without the financial regret.
          </p>
          <p>
            Whether you're approaching a milestone birthday, planning a celebration, or reflecting on past birthday spending, our free tool helps you develop healthier celebration habits that honor both your desire to celebrate and your long-term financial wellbeing.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default BirthdaySplurgeLanding;
